import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentType } from '../fields/ComponentType';
import { EnergyCapacity } from '../fields/EnergyCapacity';
import { PortPurpose } from '../fields';
import { environmentalWithCoolingMethod } from '../component-sections/environmental';

const port = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
    description: z.string().default(''),
    purpose: PortPurpose.validator,
}).default({
    powerFlowDirection: 'bidirectional',
});

const SolutionBase = z.object({
    type: z.literal('solution').default('solution'),
    canServeAs: z.array(ComponentValidator.shape.type).default([]),
    electrical: z
        .object({
            ports: z.array(port).min(1).default([undefined]),
            energyCapacity: EnergyCapacity.validator,
        })
        .default({}),
    baseComponents: z.array(ComponentType.validator).default([]),
    compatibleProducts: z.array(ComponentValidator.shape.id).default([]),
    environmental: environmentalWithCoolingMethod,
});

export const SolutionValidator = ComponentValidator.merge(SolutionBase);
export const SolutionPayloadValidator = ComponentPayloadValidator.merge(SolutionBase);

export const SolutionDefinition: ComponentDefinition = {
    type: 'solution',
    name: 'Solution',
    description:
        'A solution is a combination of components that together perform one or more functions. Examples of solutions are: battery storage systems combining battery modules and AC/DC converters, a rack with multiple power conversion units,... By definition, a solution is the most generic type of component that can have any number of AC and DC ports and can have integrated storage capacity.',
    plural: 'Solutions',
    indicator: 'SOLUTION',
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: true,
    actsAsConverter: true,
    ports: {
        min: 1,
        max: Infinity,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
    },
    canServeAsOptions: ['battery', 'converter', 'charger', 'generator', 'solar'],
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: SolutionValidator,
        payload: SolutionPayloadValidator,
    },
};

export type Solution = z.infer<typeof SolutionValidator>;
export type SolutionPayload = z.infer<typeof SolutionPayloadValidator>;
